import { whereAlpha2 } from 'iso-3166-1'
import {
  MetricDataFilters,
  MetricDataFilterSet,
  MetricReturnPeriodFilters,
  MetricScenarioType,
} from '../../../hooks/analytics'

const SCENARIO_TYPE_ORDER: MetricScenarioType[] = ['ssp', 'rcp']

function toPositiveIntegerList(value: unknown): number[] {
  const list = Array.isArray(value) ? value : value != null ? [value] : []
  const normalized = list
    .map(item => {
      const parsed = typeof item === 'number' ? item : Number.parseInt(String(item), 10)
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null
    })
    .filter((item): item is number => item !== null)

  return Array.from(new Set(normalized)).sort((left, right) => left - right)
}

function toScenarioTypes(value: unknown): MetricScenarioType[] {
  const list = Array.isArray(value) ? value : value != null ? [value] : []
  const set = new Set<MetricScenarioType>()

  list.forEach(item => {
    const normalized = String(item).trim().toLowerCase()
    if (normalized === 'ssp' || normalized === 'rcp') {
      set.add(normalized)
    }
  })

  return SCENARIO_TYPE_ORDER.filter(item => set.has(item))
}

function sanitizeReturnPeriodFilters(
  value: MetricReturnPeriodFilters | null | undefined,
): MetricReturnPeriodFilters | undefined {
  if (!value) return undefined

  const values = toPositiveIntegerList(value.values)
  if (values.length > 0) {
    return { values }
  }

  const min =
    typeof value.min === 'number' && Number.isInteger(value.min) && value.min > 0 ? value.min : undefined
  const max =
    typeof value.max === 'number' && Number.isInteger(value.max) && value.max > 0 ? value.max : undefined

  if (min == null && max == null) {
    return undefined
  }

  if (min != null && max != null && min > max) {
    return { min: max, max: min }
  }

  return {
    ...(min != null ? { min } : {}),
    ...(max != null ? { max } : {}),
  }
}

export function sanitizeMetricDataFilterSet(
  value: MetricDataFilterSet | null | undefined,
): MetricDataFilterSet | undefined {
  if (!value) return undefined

  const scenarioTypes = toScenarioTypes(value.scenario_type)
  const returnPeriod = sanitizeReturnPeriodFilters(value.return_period)

  const cleaned: MetricDataFilterSet = {}
  if (scenarioTypes.length > 0) {
    cleaned.scenario_type = scenarioTypes
  }
  if (returnPeriod) {
    cleaned.return_period = returnPeriod
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

export function sanitizeMetricDataFilters(
  value: MetricDataFilters | null | undefined,
): MetricDataFilters | undefined {
  if (!value) return undefined

  const root = sanitizeMetricDataFilterSet(value)
  const countryOverrides = value.country_overrides || {}
  const cleanedOverrides: Record<string, MetricDataFilterSet> = {}

  Object.entries(countryOverrides).forEach(([rawCountryCode, filterSet]) => {
    const countryCode = normalizeCountryCode(rawCountryCode)
    if (!countryCode) return

    const cleaned = sanitizeMetricDataFilterSet(filterSet)
    if (cleaned) {
      cleanedOverrides[countryCode] = cleaned
    }
  })

  const cleaned: MetricDataFilters = {}
  if (root?.scenario_type?.length) {
    cleaned.scenario_type = root.scenario_type
  }
  if (root?.return_period) {
    cleaned.return_period = root.return_period
  }
  if (Object.keys(cleanedOverrides).length > 0) {
    cleaned.country_overrides = cleanedOverrides
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

export function cloneMetricDataFilters(value: MetricDataFilters | null | undefined): MetricDataFilters | null {
  const cleaned = sanitizeMetricDataFilters(value)
  if (!cleaned) return null

  return {
    ...(cleaned.return_period ? { return_period: { ...cleaned.return_period } } : {}),
    ...(cleaned.scenario_type ? { scenario_type: [...cleaned.scenario_type] } : {}),
    ...(cleaned.country_overrides
      ? {
          country_overrides: Object.fromEntries(
            Object.entries(cleaned.country_overrides).map(([countryCode, filterSet]) => [
              countryCode,
              {
                ...(filterSet.return_period ? { return_period: { ...filterSet.return_period } } : {}),
                ...(filterSet.scenario_type ? { scenario_type: [...filterSet.scenario_type] } : {}),
              },
            ]),
          ),
        }
      : {}),
  }
}

export function hasMetricDataFilters(value: MetricDataFilters | null | undefined): boolean {
  return Boolean(sanitizeMetricDataFilters(value))
}

export function normalizeCountryCode(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!raw) return ''

  const optionMatch = raw.match(/^([A-Za-z]{2})\s*-/)
  const rawCode = optionMatch?.[1] ?? raw
  const normalized = rawCode.toUpperCase().replace(/[^A-Z]/g, '')
  if (normalized.length !== 2) {
    return ''
  }

  return whereAlpha2(normalized)?.alpha2 ?? ''
}

export function getCountryNameByCode(value: unknown): string | null {
  const countryCode = normalizeCountryCode(value)
  if (!countryCode) return null
  return whereAlpha2(countryCode)?.country ?? null
}
